import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Clock,
  Pause,
  Search,
  Shield,
  XCircle,
} from 'lucide-react';
import { productsApi } from '@/api';
import type { MyAccessGrant } from '@/api/products';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PageHeader, Segmented, StatusPill } from '@/components/page';
import {
  AppMark,
  CardHead,
  ConfirmDialog,
  EmptyState,
  KV,
  Tag,
  TagList,
  daysUntil,
  fmtDate,
  grantStateOf,
  GrantPill,
  type GrantState,
} from '@/components/products/shared';
import { cn } from '@/lib/utils';
import { useAuth, useMyAccess, useMyAccessOverview, useToast } from '@/hooks';

type Filter = 'all' | 'active' | 'attention';
const ATTENTION: GrantState[] = ['expiring', 'expired', 'suspended'];

interface Row {
  g: MyAccessGrant;
  state: GrantState;
}

export function MyAccessPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.is_super_admin === true;
  const location = useLocation();
  const { data: grants = [], isLoading, isError, refetch } = useMyAccess();
  const { data: overview, isLoading: overviewLoading } = useMyAccessOverview();

  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [revoking, setRevoking] = useState<MyAccessGrant | null>(null);

  const revoke = useMutation({
    mutationFn: (productId: string) => productsApi.revokeMyAccess(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-access'] });
      toast({ title: 'Access revoked', description: 'You no longer have access to this app.' });
      setRevoking(null);
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Revoke failed',
        description: error instanceof Error ? error.message : 'Failed to revoke access',
      });
    },
  });

  const rows: Row[] = useMemo(
    () =>
      grants
        .map((g) => ({
          g,
          state: grantStateOf({ is_active: g.isGrantActive, expires_at: g.expiresAt }),
        }))
        .sort(
          (a, b) =>
            Number(ATTENTION.includes(b.state)) - Number(ATTENTION.includes(a.state)) ||
            a.g.name.localeCompare(b.g.name)
        ),
    [grants]
  );

  const counts = {
    all: rows.length,
    active: rows.filter((r) => r.state === 'active').length,
    attention: rows.filter((r) => ATTENTION.includes(r.state)).length,
  };

  const term = search.trim().toLowerCase();
  const shown = rows.filter(
    (r) =>
      (filter === 'all' || (filter === 'active' ? r.state === 'active' : ATTENTION.includes(r.state))) &&
      (!term || r.g.name.toLowerCase().includes(term) || r.g.slug.includes(term))
  );

  // Deep link from the overview: /access#<productId>
  const target = location.hash.replace('#', '');
  useEffect(() => {
    if (!target || !rows.length) return;
    document.getElementById(`grant-${target}`)?.scrollIntoView({ block: 'start' });
  }, [target, rows.length]);

  return (
    <div>
      <PageHeader
        title="My access"
        description={
          <>
            Apps you can sign in to and what each one lets you do. Roles and permissions reach an
            app’s access token when it requests the <code className="text-[12.5px]">products</code>{' '}
            scope.
          </>
        }
      />

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Segmented<Filter>
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All', count: counts.all },
                { value: 'active', label: 'Active', count: counts.active },
                { value: 'attention', label: 'Needs attention', count: counts.attention },
              ]}
            />
            <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                aria-label="Search apps"
                placeholder="Search apps"
                autoComplete="off"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-4" aria-busy="true" aria-label="Loading your access">
              {[0, 1].map((i) => (
                <div key={i} className="h-56 animate-pulse rounded-lg border border-border bg-foreground/[0.03]" />
              ))}
            </div>
          ) : isError ? (
            <Card>
              <EmptyState
                icon={AlertTriangle}
                title="Couldn’t load your access"
                action={
                  <Button variant="outline" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              />
            </Card>
          ) : rows.length === 0 ? (
            <Card>
              <EmptyState
                icon={Shield}
                title="No app access yet"
                body="An organization admin grants app roles. Once they do, each app appears here with its roles and expiry."
              />
            </Card>
          ) : shown.length === 0 ? (
            <Card>
              <EmptyState
                icon={Search}
                title="No grants match"
                body="Try another filter or clear the search."
                action={
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearch('');
                      setFilter('all');
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            </Card>
          ) : (
            shown.map((r) => (
              <GrantCard
                key={r.g.productId}
                row={r}
                highlighted={target === r.g.productId}
                isAdmin={isAdmin}
                onRevoke={() => setRevoking(r.g)}
              />
            ))
          )}
        </div>

        <div className="space-y-5">
          <Card className="overflow-hidden">
            <CardHead title="Organizations" sub="Where you’re a member." />
            {overviewLoading ? (
              <div className="h-20 animate-pulse bg-foreground/[0.03]" />
            ) : overview?.memberships.length ? (
              <div className="divide-y divide-border">
                {overview.memberships.map((m) => (
                  <Link
                    key={m.organizationId}
                    to={`/organizations/${m.organizationId}`}
                    className="flex items-center gap-3 px-[18px] py-3 hover:bg-foreground/[0.03]"
                  >
                    <AppMark name={m.name} />
                    <span className="min-w-0 flex-1">
                      <b className="block truncate font-semibold">{m.name}</b>
                      <span className="block text-xs text-muted-foreground">
                        Joined {fmtDate(m.joinedAt)}
                      </span>
                    </span>
                    <Tag solid>{m.role}</Tag>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Building2}
                title="Not in an organization"
                body="Ask an owner to invite you, or create your own."
                action={
                  <Button variant="outline" asChild>
                    <Link to="/organizations/new">New organization</Link>
                  </Button>
                }
              />
            )}
          </Card>

          <Card className="overflow-hidden">
            <CardHead title="Also available" sub="No grant needed." />
            {overviewLoading ? (
              <div className="h-20 animate-pulse bg-foreground/[0.03]" />
            ) : overview?.available.length ? (
              <div className="divide-y divide-border">
                {overview.available.map((p) => {
                  const body = (
                    <>
                      <AppMark name={p.name} />
                      <span className="min-w-0 flex-1">
                        <b className="block truncate font-semibold">{p.name}</b>
                        <span className="block truncate text-xs text-muted-foreground">
                          {p.via === 'open' ? (
                            'Open to any signed-in user'
                          ) : (
                            <>
                              Via {p.organizationName} membership
                              {p.defaultRole && (
                                <>
                                  {' · role '}
                                  <span className="font-mono">{p.defaultRole}</span>
                                </>
                              )}
                            </>
                          )}
                        </span>
                      </span>
                      <StatusPill tone="ok">Active</StatusPill>
                    </>
                  );
                  return isAdmin ? (
                    <Link
                      key={p.productId}
                      to={`/products/${p.productId}`}
                      className="flex items-center gap-3 px-[18px] py-3 hover:bg-foreground/[0.03]"
                    >
                      {body}
                    </Link>
                  ) : (
                    <div key={p.productId} className="flex items-center gap-3 px-[18px] py-3">
                      {body}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="p-[18px] text-xs text-muted-foreground">
                Every app you can reach is listed under your grants.
              </p>
            )}
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Revoke your access to ${revoking?.name ?? ''}?`}
        body="You will lose access to this app until an administrator grants it again."
        confirmLabel="Revoke access"
        pendingLabel="Revoking…"
        danger
        pending={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking.productId)}
      />
    </div>
  );
}

function Notice({
  tone,
  icon: Icon,
  title,
  children,
}: {
  tone: 'warn' | 'bad';
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border p-3.5',
        tone === 'warn' ? 'border-warning/40 bg-warning/[0.08]' : 'border-destructive/40 bg-destructive/[0.08]'
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone === 'warn' ? 'text-warning' : 'text-destructive')} />
      <div>
        <b className="text-[13.5px] font-semibold">{title}</b>
        <p className="text-[13px] text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function GrantCard({
  row: { g, state },
  highlighted,
  isAdmin,
  onRevoke,
}: {
  row: Row;
  highlighted: boolean;
  isAdmin: boolean;
  onRevoke: () => void;
}) {
  const days = daysUntil(g.expiresAt);
  const effective = g.effectivePermissions ?? g.permissions;
  return (
    <Card
      id={`grant-${g.productId}`}
      className={cn('overflow-hidden', highlighted && 'ring-2 ring-primary/50')}
    >
      <div className="flex items-start justify-between gap-3 border-b border-border px-[18px] py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          <AppMark name={g.name} />
          <div className="min-w-0">
            <b className="block truncate font-semibold">{g.name}</b>
            <span className="block truncate text-xs text-muted-foreground">
              {g.organizationName ? `${g.organizationName} · ` : ''}
              <code className="text-[11.5px]">{g.slug}</code>
            </span>
          </div>
        </div>
        <GrantPill state={state} />
      </div>
      <div className="space-y-4 p-[18px]">
        {state === 'expiring' && (
          <Notice tone="warn" icon={Clock} title={`Access ends in ${days} days`}>
            Ask an admin{g.organizationName ? ` of ${g.organizationName}` : ''} to extend it if you
            still need it.
          </Notice>
        )}
        {state === 'expired' && (
          <Notice tone="bad" icon={XCircle} title="Access expired">
            Sign-ins to this app are refused. An organization admin can renew the grant.
          </Notice>
        )}
        {state === 'suspended' && (
          <Notice tone="warn" icon={Pause} title="Access suspended">
            Your grant is kept but sign-ins are refused until an admin resumes it.
          </Notice>
        )}
        {!g.isActive && (
          <Notice tone="warn" icon={AlertTriangle} title="This app is deactivated">
            Sign-ins are refused for everyone until it is reactivated.
          </Notice>
        )}
        <KV
          rows={[
            { label: 'Roles', value: <TagList items={g.roles} solid /> },
            { label: 'Direct permissions', value: <TagList items={g.permissions} /> },
            { label: 'Effective permissions', value: <TagList items={effective} /> },
            {
              label: 'Granted',
              value: (
                <span>
                  {fmtDate(g.grantedAt)}
                  {g.grantedByName && (
                    <span className="text-xs text-muted-foreground"> · by {g.grantedByName}</span>
                  )}
                </span>
              ),
            },
            {
              label: 'Expires',
              value: g.expiresAt ? (
                <span>
                  {fmtDate(g.expiresAt)}
                  <span className="text-xs text-muted-foreground">
                    {' · '}
                    {days !== null && days > 0 ? `in ${days} days` : `${Math.abs(days ?? 0)} days ago`}
                  </span>
                </span>
              ) : (
                'Never'
              ),
            },
          ]}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-[18px] py-2.5">
        <Button variant="ghost" size="sm" onClick={onRevoke} className="text-destructive hover:text-destructive">
          Revoke my access
        </Button>
        {isAdmin && (
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/products/${g.productId}`}>
              Open app details
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>
    </Card>
  );
}
